import { createTheme, alpha } from '@mui/material/styles';

// Design tokens. Outcome colours were validated for colour-blind separation and
// contrast against `surface`; keep them for home/draw/away data only.
export const tokens = {
  bg: '#0B0F14',
  surface: '#121821',
  surfaceRaised: '#18202B',
  border: 'rgba(255,255,255,0.07)',
  borderStrong: 'rgba(255,255,255,0.14)',
  text: '#E8EDF3',
  textMuted: '#8B98A9',
  textFaint: '#5D6878',
  accent: '#8B7CFF',
  outcome: {
    home: '#24A874',
    draw: '#BF8420',
    away: '#4A8DE0',
  },
  status: {
    good: '#3FB950',
    bad: '#F85149',
    live: '#FF5D5D',
    warn: '#D29922',
  },
  radius: 14,
};

export const outcomeLabel = { home: 'Home', draw: 'Draw', away: 'Away' };

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: tokens.accent, contrastText: '#0B0F14' },
    secondary: { main: tokens.outcome.away },
    success: { main: tokens.status.good },
    error: { main: tokens.status.bad },
    warning: { main: tokens.status.warn },
    background: { default: tokens.bg, paper: tokens.surface },
    text: { primary: tokens.text, secondary: tokens.textMuted, disabled: tokens.textFaint },
    divider: tokens.border,
  },
  shape: { borderRadius: tokens.radius },
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
    h1: { fontFamily: '"Space Grotesk", "Inter", sans-serif', fontWeight: 600 },
    h2: { fontFamily: '"Space Grotesk", "Inter", sans-serif', fontWeight: 600 },
    h3: { fontFamily: '"Space Grotesk", "Inter", sans-serif', fontWeight: 600, letterSpacing: '-0.02em' },
    h4: { fontFamily: '"Space Grotesk", "Inter", sans-serif', fontWeight: 600, letterSpacing: '-0.02em', fontSize: '1.75rem' },
    h5: { fontFamily: '"Space Grotesk", "Inter", sans-serif', fontWeight: 600, letterSpacing: '-0.01em', fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1rem' },
    subtitle2: { fontWeight: 600, fontSize: '0.8125rem' },
    overline: { fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6875rem', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg,
          fontFeatureSettings: '"tnum" 1, "cv11" 1',
          WebkitFontSmoothing: 'antialiased',
        },
        '::selection': { background: alpha(tokens.accent, 0.35) },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { border: `1px solid ${tokens.border}`, backgroundColor: tokens.surface },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 10, paddingInline: 16 } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: tokens.surfaceRaised,
          '& fieldset': { borderColor: tokens.border },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: tokens.surfaceRaised, border: `1px solid ${tokens.borderStrong}`, fontSize: 12 },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { border: `1px solid ${tokens.border}` } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44 } },
    },
  },
});

export default theme;
